import { useState, useCallback, type MouseEvent, type ChangeEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

// @mui
import {
	Card,
	Table,
	Stack,
	Paper,
	Button,
	Popover,
	TableRow,
	LinearProgress,
	MenuItem,
	TableBody,
	TableCell,
	Container,
	Typography,
	IconButton,
	TableContainer,
	TablePagination,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import Label from '../../components/label';
import { ListHead, ListToolbar, type HeadCell } from '../../components/list';

import { useGetSuppliersQuery, useDelSupplierMutation } from '../../slices/apis/testingCentreApi';
import { applySortFilter, getComparator, type Order } from '../../utils/tableSorter';
import type { SupplierProps } from '../../models';

// ----------------------------------------------------------------------

const TABLE_HEAD: HeadCell[] = [
	{ id: 'id', label: 'ID', align: 'left' },
	{ id: 'name', label: 'Name', align: 'left' },
	{ id: 'email', label: 'Email', align: 'left' },
	{ id: 'phone', label: 'Phone', align: 'left' },
	{ id: 'isBulk', label: '', align: 'left' },
	{ id: '', label: '', align: 'right' },
];

export default function Suppliers() {
	const [menuOpen, setMenuOpen] = useState<HTMLElement | null>(null);
	const [page, setPage] = useState<number>(0);
	const [order, setOrder] = useState<Order>('asc');
	const [selected] = useState<string[]>([]);
	const [filterName, setFilterName] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [orderBy, setOrderBy] = useState<keyof SupplierProps>('name');
	const [selectedSupplierId, setSelectedSupplierId] = useState<string | number | null>(null);
	const navigate = useNavigate();
	const { data, isLoading } = useGetSuppliersQuery();
	const [delSupplier] = useDelSupplierMutation();

	let emptyRows = 0;
	let filteredRequests: SupplierProps[] = [];
	let isNotFound = !filteredRequests.length && !!filterName;

	const handleOpenMenu = (event: MouseEvent<HTMLElement>, id: string | number) => {
		setMenuOpen(event.currentTarget);
		setSelectedSupplierId(id);
	};

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleRequestSort = (event: MouseEvent<unknown>, property: keyof SupplierProps) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleDeleteSupplier = () => {
		delSupplier(selectedSupplierId);
		setMenuOpen(null);
	};

	const handleEditSupplier = () => {
		navigate(`/dashboard/admin/suppliers/${selectedSupplierId}/edit`);
		setMenuOpen(null);
	};

	const handleViewSupplier = () => {
		navigate(`/dashboard/admin/suppliers/${selectedSupplierId}/view`);
		setMenuOpen(null);
	};

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	if (isLoading) {
		return (
			<>
				<Helmet>
					<title>Testing Centres - {process.env.REACT_APP_ORG}</title>
				</Helmet>
				<Container sx={{ width: '75%' }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
						<Typography variant="h4" gutterBottom>
							Testing Centres
						</Typography>
					</Stack>
					<LinearProgress />
				</Container>
			</>
		);
	}

	if (data) {
		emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
		filteredRequests = applySortFilter({
			list: data,
			comparator: getComparator(order, orderBy),
			query: filterName,
			orderBy: 'name',
		});
		isNotFound = !filteredRequests.length && !!filterName;
	}

	return (
		<>
			<Helmet>
				<title>Testing Centres - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container sx={{ width: '75%' }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="landing_page_heading" gutterBottom>
						Testing Centres
					</Typography>
					<Button
						variant="contained"
						data-route="/dashboard/admin/suppliers/new"
						onClick={handleNavigate}
						startIcon={<Iconify icon="eva:plus-fill" />}
					>
						New Testing Centre
					</Button>
				</Stack>

				<Card>
					<ListToolbar
						numSelected={selected.length}
						filterName={filterName}
						onFilterName={handleFilterByName}
						placeHolderText={'Testing Centre name...'}
					/>

					<Scrollbar>
						<TableContainer>
							<Table>
								<ListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
								<TableBody>
									{filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
										const { id = '', name, email, phone, isBulk } = row;

										const selectedRequest = selected.indexOf(name) !== -1;

										return (
											<TableRow hover key={id} tabIndex={-1} selected={selectedRequest}>
												<TableCell align="left">
													<Typography variant="subtitle2">{id}</Typography>
												</TableCell>
												<TableCell align="left">{name}</TableCell>
												<TableCell align="left">{email}</TableCell>
												<TableCell align="left">{phone}</TableCell>
												<TableCell align="left">{isBulk && <Label color="success">BULK</Label>}</TableCell>
												<TableCell align="right">
													<IconButton size="large" color="inherit" onClick={(event) => handleOpenMenu(event, id)}>
														<Iconify icon={'eva:more-vertical-fill'} />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
									{emptyRows > 0 && (
										<TableRow style={{ height: 53 * emptyRows }}>
											<TableCell colSpan={6} />
										</TableRow>
									)}
								</TableBody>

								{isNotFound && (
									<TableBody>
										<TableRow>
											<TableCell align="center" colSpan={6} sx={{ py: 3 }}>
												<Paper
													sx={{
														textAlign: 'center',
													}}
												>
													<Typography variant="h6" paragraph>
														Not found
													</Typography>

													<Typography variant="body2">
														No results found for &nbsp;
														<strong>&quot;{filterName}&quot;</strong>.
														<br /> Check your typos or use complete words
													</Typography>
												</Paper>
											</TableCell>
										</TableRow>
									</TableBody>
								)}
							</Table>
						</TableContainer>
					</Scrollbar>

					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={data ? data.length : 0}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Card>
			</Container>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleViewSupplier}>
					<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
					View
				</MenuItem>
				<MenuItem onClick={handleEditSupplier}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					Edit
				</MenuItem>
				<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteSupplier}>
					<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Popover>
		</>
	);
}
